import React, { useEffect, useState, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import type { RootState, AppDispatch } from "../../../../../store/store";
import { fetchLastStockSeqAsync, createStockAsync, fetchStockListAsync, clearStockMessages, fetchInstrumentsAsync, createInstrumentAsync } from "../../../../../store/Slice/stockSlice";
import { toast } from "react-toastify";
import { MdDeleteOutline } from "react-icons/md";
import { motion } from "framer-motion";

// instruments are now fetched from backend

type StockEntry = {
  id: number;
  instrument: { name: string; code: string };
  parameters: string;
  quantity: number;
  responsiblePerson: string;
  approvedBy: string;
  remark: string;
};

const emptyStock = (id: number, instrument = { name: "", code: "" }): StockEntry => ({
  id,
  instrument,
  parameters: "",
  quantity: 1,
  responsiblePerson: "",
  approvedBy: "",
  remark: "",
});

const StockForm: React.FC<{ onClose?: () => void }> = ({ onClose }) => {
  const dispatch = useDispatch<AppDispatch>();
  const { lastSeq, lastOrder, error, message, instruments } = useSelector((state: RootState) => state.stock);
  const { user } = useSelector((state: RootState) => state.auth);

  const getErrorMessage = (err: unknown): string => {
    if (typeof err === "string") return err;
    if (err && typeof err === "object") {
      const e = err as Record<string, unknown>;
      const m = e["message"] ?? e["payload"] ?? e["error"];
      if (typeof m === "string") return m;
      try {
        return JSON.stringify(e);
      } catch {
        return String(e);
      }
    }
    return String(err);
  };

  const [stocks, setStocks] = useState<StockEntry[]>([emptyStock(1)]);
  const [submitting, setSubmitting] = useState(false);
  const [newInstrumentName, setNewInstrumentName] = useState("");
  const [newInstrumentCode, setNewInstrumentCode] = useState("");

  // fetch last seq on mount
  useEffect(() => {
    dispatch(fetchLastStockSeqAsync());
    dispatch(clearStockMessages());
    dispatch(fetchInstrumentsAsync());
  }, [dispatch]);

  useEffect(() => {
    if (message) {
      toast.success(message);
      dispatch(clearStockMessages());
    }
    if (error) {
      const msg = typeof error === "string" ? error : JSON.stringify(error);
      toast.error(msg || "An error occurred");
      dispatch(clearStockMessages());
    }
  }, [message, error, dispatch]);

  // when instruments load, ensure any empty instrument in stocks is set
  useEffect(() => {
    if (Array.isArray(instruments) && instruments.length > 0) {
      setStocks((prev) =>
        prev.map((s) => ({
          ...s,
          instrument: s.instrument?.code ? s.instrument : instruments[0],
        }))
      );
    }
  }, [instruments]);

  const handleAddInstrument = async () => {
    if (!newInstrumentName.trim() || !newInstrumentCode.trim()) {
      toast.error("Name and code required");
      return;
    }
    try {
      await dispatch(createInstrumentAsync({ name: newInstrumentName.trim(), code: newInstrumentCode.trim() })).unwrap();
      toast.success("Instrument added");
      setNewInstrumentName("");
      setNewInstrumentCode("");
      dispatch(fetchInstrumentsAsync());
    } catch (err: unknown) {
      toast.error(getErrorMessage(err) || "Failed to add instrument");
    }
  };

  const computePreview = useCallback((stock: StockEntry, index: number) => {
    const seq = (lastSeq || 0) + (index + 1);
    const now = new Date();
    const month = String(now.getMonth() + 1).padStart(2, "0");
    const year = String(now.getFullYear()).slice(-2);
    return `STK/${stock.instrument.code}/${month}/${year}/${String(seq).padStart(2, "0")}`;
  }, [lastSeq]);

  const handleAddStock = () => {
    setStocks([...stocks, emptyStock(stocks.length + 1)]);
    setTimeout(() => {
      window.scrollTo({ top: document.body.scrollHeight, behavior: "smooth" });
    }, 100);
  };

  const handleRemoveStock = (id: number) => {
    if (stocks.length === 1) {
      toast.info("At least one stock entry is required");
      return;
    }
    const updated = stocks.filter((stock) => stock.id !== id);
    setStocks(updated);
    toast.success("Stock entry removed");
  };

  const handleChange = (id: number, field: keyof StockEntry, value: string | number | { name: string; code: string }) => {
    const updated = stocks.map((stock) =>
      stock.id === id ? { ...stock, [field]: value } : stock
    );
    setStocks(updated);
  };

  const validateStock = (stock: StockEntry, index: number): string | null => {
    if (!stock.responsiblePerson.trim()) {
      return `Responsible Person is required for stock #${index + 1}`;
    }
    if (!stock.approvedBy.trim()) {
      return `Approved By is required for stock #${index + 1}`;
    }
    if (stock.quantity <= 0) {
      return `Quantity must be greater than 0 for stock #${index + 1}`;
    }
    return null;
  };

  const handleSubmit = async (e?: React.FormEvent) => {
    e?.preventDefault();

    // Validate all stocks
    for (let i = 0; i < stocks.length; i++) {
      const validationError = validateStock(stocks[i], i);
      if (validationError) {
        toast.error(validationError);
        return;
      }
    }

    setSubmitting(true);
    try {
      // Submit each stock individually or as array based on your backend
      const payload = stocks.length === 1 ? stocks[0] : stocks;
      await dispatch(createStockAsync(payload)).unwrap();
      
      // refresh list
      dispatch(fetchStockListAsync());
      toast.success(`Successfully created ${stocks.length} stock entr${stocks.length > 1 ? 'ies' : 'y'}!`);
      
      if (onClose) onClose();
    } catch (err: unknown) {
      toast.error(getErrorMessage(err) || "Failed to create stock");
    } finally {
      setSubmitting(false);
      // fetch new seq to update preview
      dispatch(fetchLastStockSeqAsync());
    }
  };

  const handleClose = () => {
    if (onClose) onClose();
  };

  return (
    <div className="w-full max-h-[90vh] overflow-y-auto p-4">
      <motion.div
        initial={{ y: 40, opacity: 0 }}
        animate={{ y: 0, opacity: 1, transition: { duration: 0.3 } }}
        className="
          bg-white dark:bg-zinc-800
          w-full max-w-5xl 
          mx-auto 
          rounded-2xl 
          p-6 
          shadow-xl 
          border border-gray-300 dark:border-zinc-700 
          overflow-y-auto 
          max-h-[85vh]
        "
      >
        {/* Header */}
        <div className="flex justify-between items-center w-full">
          <h1 className="text-black text-center w-full dark:text-white font-bold lg:text-lg xl:text-lg text-sm mb-5 uppercase">
            Create Stock Entry
          </h1>
          <button
            className="text-gray-700 dark:text-white text-4xl px-3 py-2 rounded-lg cursor-pointer"
            onClick={handleClose}
            type="button"
          >
            &times;
          </button>
        </div>

        {/* Admin: Add Instrument */}
        {user?.userType === "admin" && (
          <div className="mb-4 p-4 border rounded bg-gray-50 dark:bg-zinc-900">
            <h3 className="font-semibold mb-2">Add Instrument (Admin)</h3>
            <div className="flex gap-2">
              <input value={newInstrumentName} onChange={(e) => setNewInstrumentName(e.target.value)} placeholder="Instrument Name" className="p-2 border rounded w-full" />
              <input value={newInstrumentCode} onChange={(e) => setNewInstrumentCode(e.target.value)} placeholder="Code" className="p-2 border rounded w-40" />
              <button type="button" onClick={handleAddInstrument} className="px-3 py-2 bg-blue-600 text-white rounded">Add</button>
            </div>
          </div>
        )}

        {/* Form */}
        <form className="flex flex-col gap-4" onSubmit={handleSubmit}>
          {stocks.map((stock, index) => (
            <section key={stock.id} className="flex flex-col gap-2 lg:text-lg xl:text-lg text-sm">
              {/* Section Header */}
              <div className="flex justify-between items-center">
                <h1 className="text-start font-bold text-xl text-blue-500 mb-4 uppercase">
                  Stock #{String(index + 1).padStart(2, "0")}
                </h1>
                {stocks.length > 1 && (
                  <button
                    type="button"
                    onClick={() => handleRemoveStock(stock.id)}
                    className="text-red-600 hover:text-red-700 text-xl hover:bg-red-200 p-2 duration-300 cursor-pointer rounded-lg mb-4"
                  >
                    <MdDeleteOutline />
                  </button>
                )}
              </div>

              <div className="border-2 rounded-lg p-5 shadow-lg">
                {/* Instrument Selection */}
                <div className="relative z-0 w-full mb-5 group">
                  <select
                    name="instrument"
                    id={`instrument_${stock.id}`}
                    value={stock.instrument.code}
                    onChange={(e) => {
                      const found = (instruments || []).find((i) => i.code === e.target.value);
                      handleChange(stock.id, "instrument", (found as { name: string; code: string }) || { name: e.target.value, code: e.target.value });
                    }}
                    className="block pt-5 px-0 w-full lg:text-lg xl:text-lg text-sm text-gray-900 bg-transparent border-0 border-b-2 border-gray-300 appearance-none dark:text-white dark:border-gray-600 dark:focus:border-white focus:outline-none focus:ring-0 focus:border-[#0A2975] peer"
                    required
                  >
                    {(instruments || []).map((i) => (
                      <option key={i.code} value={i.code}>
                        {i.name} ({i.code})
                      </option>
                    ))}
                    {/* if no instruments, show a fallback */}
                    {(instruments || []).length === 0 && (
                      <option value="">No instruments available</option>
                    )}
                  </select>
                  <label
                    htmlFor={`instrument_${stock.id}`}
                    className="absolute lg:text-lg xl:text-lg text-sm text-black dark:text-gray-400 duration-300 transform scale-75 top-0 left-0 origin-[0] peer-placeholder-shown:scale-100 peer-placeholder-shown:translate-y-2.5 peer-focus:scale-75 peer-focus:-translate-y-4 peer-focus:text-[#0A2975] peer-focus:dark:text-white"
                  >
                    Select Instrument <span className="text-red-500 m-2">*</span>
                  </label>
                </div>

                {/* Preview Order Number */}
                <div className="flex flex-col items-start mb-5">
                  <div className="mb-2 text-sm text-[#0A2975] font-semibold">
                    Stock Order Number:{" "}
                    <span className="font-semibold">{computePreview(stock, index)}</span>
                  </div>
                  {index === 0 && lastOrder != null && (
                    <div className="text-xs text-gray-600">
                      Last sequence: {lastSeq || 0} • Last order: {String(lastOrder)}
                    </div>
                  )}
                </div>

                {/* Quantity */}
                <div className="relative z-0 w-full mb-5 group">
                  <input
                    type="number"
                    name="quantity"
                    id={`quantity_${stock.id}`}
                    min="1"
                    value={stock.quantity}
                    onChange={(e) => handleChange(stock.id, "quantity", Number(e.target.value))}
                    className="block pt-5 px-0 w-full lg:text-lg xl:text-lg text-sm text-gray-900 bg-transparent border-0 border-b-2 border-gray-300 appearance-none dark:text-white dark:border-gray-600 dark:focus:border-white focus:outline-none focus:ring-0 focus:border-[#0A2975] peer"
                    placeholder=" "
                    required
                  />
                  <label
                    htmlFor={`quantity_${stock.id}`}
                    className="absolute lg:text-lg xl:text-lg text-sm text-black dark:text-gray-400 duration-300 transform scale-75 top-0 left-0 origin-[0] peer-placeholder-shown:scale-100 peer-placeholder-shown:translate-y-2.5 peer-focus:scale-75 peer-focus:-translate-y-4 peer-focus:text-[#0A2975] peer-focus:dark:text-white"
                  >
                    Quantity <span className="text-red-500 m-2">*</span>
                  </label>
                </div>

                {/* Parameters */}
                <div className="relative z-0 w-full mb-5 group">
                  <input
                    type="text"
                    name="parameters"
                    id={`parameters_${stock.id}`}
                    value={stock.parameters}
                    onChange={(e) => handleChange(stock.id, "parameters", e.target.value)}
                    className="block pt-5 px-0 w-full lg:text-lg xl:text-lg text-sm text-gray-900 bg-transparent border-0 border-b-2 border-gray-300 appearance-none dark:text-white dark:border-gray-600 dark:focus:border-white focus:outline-none focus:ring-0 focus:border-[#0A2975] peer"
                    placeholder=" "
                  />
                  <label
                    htmlFor={`parameters_${stock.id}`}
                    className="absolute lg:text-lg xl:text-lg text-sm text-black dark:text-gray-400 duration-300 transform scale-75 top-0 left-0 origin-[0] peer-placeholder-shown:scale-100 peer-placeholder-shown:translate-y-2.5 peer-focus:scale-75 peer-focus:-translate-y-4 peer-focus:text-[#0A2975] peer-focus:dark:text-white"
                  >
                    Parameters (Optional)
                  </label>
                </div>

                {/* Responsible Person */}
                <div className="relative z-0 w-full mb-5 group">
                  <input
                    type="text"
                    name="responsiblePerson"
                    id={`responsiblePerson_${stock.id}`}
                    value={stock.responsiblePerson}
                    onChange={(e) => handleChange(stock.id, "responsiblePerson", e.target.value)}
                    className="block pt-5 px-0 w-full lg:text-lg xl:text-lg text-sm text-gray-900 bg-transparent border-0 border-b-2 border-gray-300 appearance-none dark:text-white dark:border-gray-600 dark:focus:border-white focus:outline-none focus:ring-0 focus:border-[#0A2975] peer"
                    placeholder=" "
                    required
                  />
                  <label
                    htmlFor={`responsiblePerson_${stock.id}`}
                    className="absolute lg:text-lg xl:text-lg text-sm text-black dark:text-gray-400 duration-300 transform scale-75 top-0 left-0 origin-[0] peer-placeholder-shown:scale-100 peer-placeholder-shown:translate-y-2.5 peer-focus:scale-75 peer-focus:-translate-y-4 peer-focus:text-[#0A2975] peer-focus:dark:text-white"
                  >
                    Responsible Person <span className="text-red-500 m-2">*</span>
                  </label>
                </div>

                {/* Approved By */}
                <div className="relative z-0 w-full mb-5 group">
                  <input
                    type="text"
                    name="approvedBy"
                    id={`approvedBy_${stock.id}`}
                    value={stock.approvedBy}
                    onChange={(e) => handleChange(stock.id, "approvedBy", e.target.value)}
                    className="block pt-5 px-0 w-full lg:text-lg xl:text-lg text-sm text-gray-900 bg-transparent border-0 border-b-2 border-gray-300 appearance-none dark:text-white dark:border-gray-600 dark:focus:border-white focus:outline-none focus:ring-0 focus:border-[#0A2975] peer"
                    placeholder=" "
                    required
                  />
                  <label
                    htmlFor={`approvedBy_${stock.id}`}
                    className="absolute lg:text-lg xl:text-lg text-sm text-black dark:text-gray-400 duration-300 transform scale-75 top-0 left-0 origin-[0] peer-placeholder-shown:scale-100 peer-placeholder-shown:translate-y-2.5 peer-focus:scale-75 peer-focus:-translate-y-4 peer-focus:text-[#0A2975] peer-focus:dark:text-white"
                  >
                    Approved By <span className="text-red-500 m-2">*</span>
                  </label>
                </div>

                {/* Remark */}
                <div className="relative z-0 w-full mb-5 group">
                  <textarea
                    name="remark"
                    id={`remark_${stock.id}`}
                    value={stock.remark}
                    onChange={(e) => handleChange(stock.id, "remark", e.target.value)}
                    className="block pt-5 px-0 w-full lg:text-lg xl:text-lg text-sm text-gray-900 bg-transparent border-0 border-b-2 border-gray-300 appearance-none dark:text-white dark:border-gray-600 dark:focus:border-white focus:outline-none focus:ring-0 focus:border-[#0A2975] peer"
                    placeholder=" "
                    rows={3}
                  />
                  <label
                    htmlFor={`remark_${stock.id}`}
                    className="absolute lg:text-lg xl:text-lg text-sm text-black dark:text-gray-400 duration-300 transform scale-75 top-0 left-0 origin-[0] peer-placeholder-shown:scale-100 peer-placeholder-shown:translate-y-2.5 peer-focus:scale-75 peer-focus:-translate-y-4 peer-focus:text-[#0A2975] peer-focus:dark:text-white"
                  >
                    Remark (Optional)
                  </label>
                </div>
              </div>
            </section>
          ))}

          {/* Action Buttons */}
          <div className="flex justify-between">
            <button
              type="button"
              onClick={handleAddStock}
              className="text-end max-w-fit bg-gray-200 text-black px-4 py-2 lg:text-lg xl:text-lg text-sm rounded-md hover:bg-gray-300 cursor-pointer transition duration-300"
              disabled={submitting}
            >
              + Add Stock
            </button>

            {!submitting ? (
              <div className="justify-end flex gap-4">
                <button
                  type="submit"
                  className="text-end max-w-fit bg-[#0A2975] text-white px-3 py-1 font-semibold lg:text-xl xl:text-xl text-sm rounded-md hover:bg-[#092060] transition duration-300 cursor-pointer"
                >
                  Submit {stocks.length > 1 ? `All (${stocks.length})` : ""}
                </button>
                <button
                  type="button"
                  onClick={handleClose}
                  className="text-end max-w-fit bg-red-400 text-white px-3 py-1 font-semibold lg:text-xl xl:text-xl text-sm rounded-md hover:bg-red-500 transition duration-300 cursor-pointer"
                >
                  Cancel
                </button>
              </div>
            ) : (
              <button
                type="button"
                className="text-end max-w-fit bg-gray-400 text-white px-2 py-1 font-semibold lg:text-xl xl:text-xl text-sm rounded-md cursor-not-allowed"
                disabled
              >
                Submitting...
              </button>
            )}
          </div>
        </form>
      </motion.div>
    </div>
  );
};

export default StockForm;